<script setup lang="ts">
import { getSkillName, skillCollection, getSkillAffixClass, skillQualityDamageMag, explainSkillAffix, getSkillNameById } from '@/core/skill'
import { useUserStore } from '@/stores/modules/user'
import type { SkillItemType, SkillSellRes } from '@/types/skill'
import { equipQualityText, noPercentEquipAttrKeyList, relicElementNameMap, relicIcon, levelClassMap } from '../data'
import { BasicAttrTextEnum, SkillPriceEnum } from '@/enums'
import { computed } from 'vue'
import { formatNumber, formatNumberWithCommas } from '@/utils'
import { socketEmitAsync } from '@/utils/web-socket'
import { Lock, Unlock } from '@element-plus/icons-vue'
import { getSupremeBasicAttrValue } from '@/core/equip/supreme'
import { debuffCollection } from '@/core/skill/debuff'
import { passiveCollection } from '@/core/skill/passive'
import type { UserAttrType } from '@/types/user'
import { extraDamageCollection } from '@/core/skill/extra-damage'
import { skillSpecialCollection } from '@/core/skill/special'

const userStore = useUserStore()

const props = withDefaults(
  defineProps<{
    skillInfo: SkillItemType
    isInItems?: boolean
    isInComparison?: boolean
    showAction?: boolean
    showLock?: boolean
    showLevelType?: number
  }>(),
  {
    isInItems: true,
    isInComparison: false,
    showAction: true,
    showLock: true,
    showLevelType: 1,
  },
)
const emits = defineEmits(['clear', 'lock', 'compare', 'closeCompare', 'sell'])

async function handleEquipSkill(action: 'wear' | 'remove', index?: number) {
  if (userStore.getItemEquipTotal >= userStore.getItemCapacity) return ElMessage.warning('背包已满')
  const id = props.skillInfo.id
  const res = await socketEmitAsync<{ userAttr: UserAttrType; skillEquippedList: string[] }>({ type: 'skillAction', data: { id, action, index } })
  userStore.userAttr = res.userAttr
  userStore.skillEquippedList = res.skillEquippedList
  userStore.setRoutineEquippedList()
  emits('clear')
  if (props.isInComparison) emits('closeCompare')
  ElMessage.success(action === 'wear' ? '携带成功' : '卸下成功')
}
async function handleLock() {
  const id = props.skillInfo.id
  await socketEmitAsync<SkillItemType[]>({ type: 'lockSkill', data: { id } })
  const skill = userStore.skillList.find((item) => item.id === id)
  if (!skill) return
  skill.isLock = !skill.isLock
  emits('lock')
}
function handleSell() {
  const { skillInfo } = props
  const { quality = 'normal', skillId, isLock } = skillInfo
  if (isLock) return ElMessage.warning('技能已锁定，无法出售')
  if (quality === 'antiquity') return ElMessage.warning('远古技能无法出售')
  ElMessageBox.confirm(`<p class="affix">确定出售【<span class="${quality}">${getSkillNameById(skillId)}</span>】吗？</p>`, '提示', {
    type: 'warning',
    dangerouslyUseHTMLString: true,
    appendTo: 'body',
    confirmButtonClass: 'is-plain',
  })
    .then(async () => {
      const { skillList, gold } = await socketEmitAsync<SkillSellRes>({ type: 'sellSkill', data: { idList: [skillInfo.id] } })
      userStore.skillList = skillList
      userStore.gold = gold
      emits('clear')
      ElMessage.success('出售成功')
    })
    .catch(() => {})
}

const skillEquippedList = computed(() =>
  userStore.skillEquippedList.map((item) => {
    const skill = userStore.skillList.find((skill) => skill.id === item)
    if (!skill) return undefined
    return skill
  }),
)
const skillLevel = computed(() => {
  return userStore.getSkillRealLevel(props.skillInfo.skillId)
})
const skillLevelAddtion = computed(() => {
  return userStore.getSkillLevelAddtion(props.skillInfo.skillId)
})
const skillExpLevel = computed(() => {
  return userStore.getSkillExpLevel(props.skillInfo.skillId)
})
const showLevel = computed(() => {
  if (props.showLevelType === 1) return skillLevel.value
  if (props.showLevelType === 2) return skillExpLevel.value
  return 0
})
const levelClassNum = computed(() => {
  return Number(String(props.skillInfo.skillId).slice(0, 1))
})
const skillCollectionInfo = computed(() => skillCollection[props.skillInfo.skillId])
function getSkillDamege(value: number) {
  const { skillInfo } = props
  return Math.round(value * skillQualityDamageMag[skillInfo.quality] * skillLevelAddtion.value * userStore.getSkillAtkAddtion())
}
</script>

<template>
  <div class="skill-info affix">
    <p :class="skillInfo.quality" style="position: relative; margin-bottom: 8px; font-weight: bold">
      <span v-html="getSkillName(skillInfo, false, showLevelType, false)"></span>
      <span class="skill-lock" v-show="showAction && showLock">
        <el-button size="small" :type="skillInfo.isLock ? 'success' : 'info'" plain @click="handleLock">
          <el-icon size="20" v-show="!skillInfo.isLock"><Unlock /></el-icon>
          <el-icon size="20" v-show="skillInfo.isLock"><Lock /></el-icon>
        </el-button>
      </span>
    </p>
    <p>
      等级：<span class="info"
        >{{ showLevel }}
        <span class="second-awaken" v-show="showLevelType === 1">
          {{ skillLevel !== skillExpLevel ? `（+${skillLevel - skillExpLevel}）` : '' }}</span
        ></span
      >
    </p>
    <p>
      类型：<span class="grow" v-show="skillCollectionInfo.type === 1">主动</span
      ><span class="info" v-show="skillCollectionInfo.type === 2">被动</span>
    </p>
    <p>
      品质：<span :class="skillInfo.quality">{{ equipQualityText[skillInfo?.quality || 'normal'] }}</span> ·
      <span :class="levelClassMap[levelClassNum]">{{ levelClassNum }}阶</span>
    </p>
    <p>
      售价：<span class="gold">{{ formatNumberWithCommas(SkillPriceEnum[skillInfo.quality]) }}</span>
    </p>
    <br />
    <template v-if="skillCollectionInfo">
      <template v-if="skillCollectionInfo.type === 1">
        <p>属性：</p>
        <p>
          魔法值消耗：<span class="mp">{{ skillCollectionInfo.mp }}</span>
        </p>
        <p>
          冷却时间：<span class="grow">{{ skillCollectionInfo.cd / 1000 }}s</span>
        </p>
        <br />
      </template>
      <p>效果：</p>
      <p class="affix">
        <span v-for="eff of skillCollectionInfo.effect">
          <span v-if="eff.type === 'damage'">
            造成
            <span class="grow">{{ formatNumberWithCommas(Math.round(getSkillDamege(eff.value))) }}</span>
            （受攻击力加成） 的<span class="info">{{ relicIcon[eff.element] }}{{ relicElementNameMap[eff.element] }}属性伤害</span>
          </span>
          <span v-if="eff.type === 'debuff'">
            <span class="grow">{{ formatNumber(eff.rate, 2) }}%</span> 概率向敌方施加持续
            <span class="grow">{{ formatNumber(eff.duration / 1000, 2) }}s</span>的
            <el-popover placement="bottom" width="300px" trigger="click">
              <template #reference>
                <span class="info" style="text-decoration: underline">{{ debuffCollection[eff.buffId].name }}</span>
              </template>
              <p class="affix">
                <span class="info">LV.{{ eff.level + Math.floor(skillLevel / eff.grow) }}</span>
                ：<span v-html="debuffCollection[eff.buffId].desc(eff.level + Math.floor(skillLevel / eff.grow))"></span>
              </p>
            </el-popover>
            效果
          </span>
          <span v-if="eff.type === 'passive'">
            <span v-html="passiveCollection[eff.passiveId]?.desc(skillInfo.skillId, eff.data, skillInfo.quality)"></span>
          </span>
          <span v-if="eff.type === 'extraDamage'">
            <span
              v-html="extraDamageCollection[eff.extraDamageId]?.desc({ skillId: skillInfo.skillId, data: eff.data, quality: skillInfo.quality })"
            ></span>
          </span>
          <span v-if="eff.type === 'special'">
            <span v-html="skillSpecialCollection[eff.specialKey]?.({ data: eff.data })"></span>
          </span>
          <br />
        </span>
      </p>
    </template>
    <br />
    <template v-if="skillInfo.attrs?.length">
      <p>词缀：</p>
      <div v-for="(attr, attrIndex) of skillInfo.attrs" :key="attrIndex">
        <template v-if="attr?.basic">
          <p v-for="(val, key) of attr.basic" :key="key" :class="getSkillAffixClass(val as number)">
            <span>{{ BasicAttrTextEnum[key] }}</span>
            <span>
              +
              <span>{{ getSupremeBasicAttrValue(key, val as number) }}{{ noPercentEquipAttrKeyList.includes(key) ? '' : '%' }}</span>
            </span>
          </p>
        </template>
        <template v-else-if="attr.special">
          <p class="affix" v-for="(item, index) of explainSkillAffix([attr.special], false)" :key="index" v-html="item"></p>
        </template>
      </div>
    </template>
  </div>
  <el-row class="equip-action" v-show="isInItems && showAction">
    <el-col :span="8">
      <el-popover placement="top-start" :width="150" trigger="click" :teleported="false">
        <template #reference>
          <CommonBtn type="success" size="small">装备</CommonBtn>
        </template>
        <div class="hour-list affix">
          <el-button
            v-for="(item, index) of skillEquippedList"
            :type="item ? 'primary' : 'info'"
            size="small"
            plain
            @click="handleEquipSkill('wear', index)"
          >
            <span v-if="item" :class="item.quality" v-html="getSkillName(item, false, 0, false)"></span>
            <span v-else>（未设置）</span>
          </el-button>
        </div>
      </el-popover>
    </el-col>
    <el-col :span="8" v-if="!isInComparison">
      <el-popover placement="top-start" :width="150" trigger="click" :teleported="false">
        <template #reference>
          <CommonBtn type="primary" size="small">比对</CommonBtn>
        </template>
        <div class="hour-list affix">
          <el-button v-for="(item, index) of skillEquippedList" :type="item ? 'primary' : 'info'" size="small" plain @click="emits('compare', index)">
            <span v-if="item" :class="item.quality" v-html="getSkillName(item)"></span>
            <span v-else>（未设置）</span>
          </el-button>
        </div>
      </el-popover>
    </el-col>
    <el-col :span="8" v-if="!isInComparison">
      <CommonBtn type="warning" size="small" @done="handleSell">出售</CommonBtn>
    </el-col>
  </el-row>
  <el-row class="equip-action" v-show="!isInItems && showAction">
    <el-col :span="8">
      <CommonBtn type="danger" size="small" @done="handleEquipSkill('remove')">卸下</CommonBtn>
    </el-col>
  </el-row>
</template>

<style lang="scss" scoped>
.skill-info {
  font-size: 14px;
  height: 100%;
  overflow: auto;
  margin-bottom: 10px;
  p {
    margin: 4px 0;
  }
  .skill-lock {
    position: absolute;
    right: 4px;
    top: calc(100% + 10px);
  }
}
:deep(.hour-list) {
  .el-button:focus,
  .el-button:hover {
    background-color: unset;
  }
}
</style>
